import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../config/env';
import { getAgentDisplayInitials } from '../utils/chatwootPermissions';

function formatDateTimeCompact(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const withYear = d.getFullYear() !== now.getFullYear();
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    ...(withYear ? { year: '2-digit' } : {}),
    hour: '2-digit',
    minute: '2-digit'
  });
}

function assignmentStatusKey(row) {
  if (row?.is_resolved || row?.status === 'resolved') return 'resuelta';
  if (row?.status === 'reassigned') return 'reasignada';
  return 'abierta';
}

const ASSIGNMENT_STATUS_META = {
  resuelta: { label: 'Resuelta', cls: 'is-resolved', tone: 'success', icon: '✓', chip: 'aprobado' },
  reasignada: { label: 'Reasignada', cls: 'is-reassigned', tone: 'assign', icon: '↻', chip: 'rechazado' },
  abierta: { label: 'Abierta', cls: 'is-open', tone: 'create', icon: '●', chip: 'pendiente' }
};

function getDefaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10)
  };
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDurationMinutes(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

function statusLabel(row) {
  if (row.is_resolved || row.status === 'resolved') return 'Resuelta';
  if (row.status === 'reassigned') return 'Reasignada';
  return 'Abierta';
}

function statusClass(row) {
  if (row.is_resolved || row.status === 'resolved') return 'is-resolved';
  if (row.status === 'reassigned') return 'is-reassigned';
  return 'is-open';
}

const PERIOD_PRESETS = [
  { id: '7', label: '7 días', days: 7 },
  { id: '30', label: '30 días', days: 30 },
  { id: '90', label: '90 días', days: 90 }
];

function buildAgentLabelMaps(agentOptions) {
  const byAgentId = new Map();
  const byCrmUsername = new Map();

  for (const agent of agentOptions || []) {
    const agentId = Number(agent.id);
    const chatwootName = String(agent.name || agent.displayName || '').trim();
    const crmUsername = String(agent.crm_username || '').trim();

    if (Number.isFinite(agentId) && agentId > 0 && chatwootName) {
      byAgentId.set(agentId, chatwootName);
    }
    if (crmUsername && chatwootName) {
      byCrmUsername.set(crmUsername.toLowerCase(), chatwootName);
    }
  }

  return { byAgentId, byCrmUsername };
}

/** Ej: administrativo_2 (Juan Pérez) */
function formatUserWithChatwootName(crmLabel, maps, options = {}) {
  const crm = String(crmLabel || '').trim();
  if (!crm) return '—';

  let chatwootName = '';
  const agentId = Number(options.agentId);
  if (Number.isFinite(agentId) && agentId > 0) {
    chatwootName = maps.byAgentId.get(agentId) || '';
  }
  if (!chatwootName) {
    chatwootName = maps.byCrmUsername.get(crm.toLowerCase()) || '';
  }
  if (chatwootName && chatwootName.toLowerCase() !== crm.toLowerCase()) {
    return `${crm} (${chatwootName})`;
  }
  return crm;
}

function chatwootNameForMaps(maps, { agentId, crmLabel } = {}) {
  const id = Number(agentId);
  if (Number.isFinite(id) && id > 0) {
    const byId = maps.byAgentId.get(id);
    if (byId) return byId;
  }
  const crm = String(crmLabel || '').trim();
  if (!crm) return '';
  return maps.byCrmUsername.get(crm.toLowerCase()) || '';
}

function TracePanelLoading({ standalone, message = 'Cargando…' }) {
  return (
    <div
      className={standalone ? 'crm-ta-loading' : 'crm-trace-loading'}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="crm-gd-spinner" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

export default function ConversationAssignmentsTracePanel({
  agentOptions = [],
  onOpenConversation,
  standalone = false,
  loadingAgents = false
}) {
  const defaults = useMemo(() => getDefaultRange(), []);
  const [expanded, setExpanded] = useState(standalone || true);
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [assigneeId, setAssigneeId] = useState('');
  const [activePreset, setActivePreset] = useState('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);

  const applyPreset = useCallback((days, presetId) => {
    if (days == null || !Number.isFinite(Number(days))) {
      setActivePreset('');
      return;
    }
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - Number(days));
    setFrom(start.toISOString().slice(0, 10));
    setTo(end.toISOString().slice(0, 10));
    setActivePreset(String(presetId || ''));
  }, []);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ from, to, limit: '100' });
      if (assigneeId) params.set('assignee_id', assigneeId);
      const res = await fetch(`${API_BASE_URL}/api/chatwoot/assignments/analytics?${params}`, {
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `Error ${res.status}`);
      setAnalytics(json?.data || null);
    } catch (err) {
      setAnalytics(null);
      setError(err?.message || 'No se pudo cargar trazabilidad');
    } finally {
      setLoading(false);
    }
  }, [from, to, assigneeId]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const totals = analytics?.totals || {};
  const operators = analytics?.operators || [];
  const trace = analytics?.trace || [];
  const agentLabelMaps = useMemo(() => buildAgentLabelMaps(agentOptions), [agentOptions]);
  const showBody = standalone || expanded;

  const kpiCards = useMemo(
    () => [
      {
        label: 'Asignaciones',
        value: totals.assigned ?? 0,
        hint: 'En el período seleccionado',
        tone: ''
      },
      {
        label: 'Resueltas',
        value: totals.resolved ?? 0,
        hint: 'Cerradas por operador',
        tone: 'up'
      },
      {
        label: 'Abiertas',
        value: totals.open ?? 0,
        hint: 'Pendientes de cierre',
        tone: Number(totals.open) > 0 ? 'warn' : ''
      },
      {
        label: 'Tasa de resolución',
        value: `${totals.resolution_rate_pct ?? 0}%`,
        hint: 'Resueltas / asignadas',
        tone: ''
      },
      {
        label: 'Promedio de cierre',
        value: formatDurationMinutes(totals.avg_resolution_minutes),
        hint: 'Desde asignación a resuelta',
        tone: ''
      }
    ],
    [totals]
  );

  if (!standalone) {
    return (
      <section className={`crm-trace-embed${showBody ? ' is-expanded' : ''}`}>
        <button
          type="button"
          className="crm-trace-embed__toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className="crm-trace-embed__chev" aria-hidden="true">
            {expanded ? '▾' : '▸'}
          </span>
          <span className="crm-trace-embed__title">Trazabilidad de asignaciones</span>
          <span className="crm-trace-embed__meta">KPIs por operador</span>
        </button>
        {showBody ? (
          <TracePanelBody
            from={from}
            to={to}
            assigneeId={assigneeId}
            activePreset={activePreset}
            loading={loading}
            loadingAgents={loadingAgents}
            error={error}
            agentOptions={agentOptions}
            agentLabelMaps={agentLabelMaps}
            kpiCards={kpiCards}
            operators={operators}
            trace={trace}
            onFromChange={setFrom}
            onToChange={setTo}
            onAssigneeChange={setAssigneeId}
            onPreset={applyPreset}
            onRefresh={loadAnalytics}
            onOpenConversation={onOpenConversation}
          />
        ) : null}
      </section>
    );
  }

  return (
    <AssignmentsTracePremium
      from={from}
      to={to}
      activePreset={activePreset}
      loading={loading}
      error={error}
      totals={totals}
      operators={operators}
      trace={trace}
      agentLabelMaps={agentLabelMaps}
      onFromChange={setFrom}
      onToChange={setTo}
      onPresetClear={() => setActivePreset('')}
      onPreset={applyPreset}
      onOpenConversation={onOpenConversation}
    />
  );
}

function AssignmentsTracePremium({
  from,
  to,
  activePreset,
  loading,
  error,
  totals,
  operators,
  trace,
  agentLabelMaps,
  onFromChange,
  onToChange,
  onPresetClear,
  onPreset,
  onOpenConversation
}) {
  const [sideCollapsed, setSideCollapsed] = useState(false);
  const [opsOpen, setOpsOpen] = useState(false);
  const [opsFilter, setOpsFilter] = useState(null);
  const [opsPos, setOpsPos] = useState({ top: 0, left: 0 });
  const opsBtnRef = useRef(null);
  const [convSearch, setConvSearch] = useState('');
  const [convStatus, setConvStatus] = useState('all');
  const [convPageSize, setConvPageSize] = useState(6);
  const [convPage, setConvPage] = useState(1);
  const [logSearch, setLogSearch] = useState('');
  const [logPageSize, setLogPageSize] = useState(10);
  const [logPage, setLogPage] = useState(1);
  const [openLogId, setOpenLogId] = useState(null);

  const operatorLabel = useCallback(
    (op) => formatUserWithChatwootName(op.operator_name, agentLabelMaps, { agentId: op.agent_id }),
    [agentLabelMaps]
  );

  const grouped = useMemo(() => {
    const map = new Map();
    const order = [];
    for (const row of trace) {
      const id = row?.conversation_id;
      if (id == null) continue;
      if (!map.has(id)) {
        map.set(id, {
          conversationId: id,
          paciente: row.patient_display_name,
          events: []
        });
        order.push(id);
      }
      map.get(id).events.push(row);
    }
    return order.map((id) => {
      const g = map.get(id);
      const latest = g.events[0] || {};
      return { ...g, statusKey: assignmentStatusKey(latest), latest };
    });
  }, [trace]);

  const opsFilterConvIds = useMemo(() => {
    if (opsFilter == null) return null;
    const set = new Set();
    for (const row of trace) {
      if (row?.conversation_id == null) continue;
      if (Number(row.assignee_agent_id) === Number(opsFilter)) set.add(row.conversation_id);
    }
    return set;
  }, [trace, opsFilter]);

  const logFiltered = useMemo(() => {
    const base = opsFilterConvIds ? grouped.filter((g) => opsFilterConvIds.has(g.conversationId)) : grouped;
    const q = logSearch.trim().toLowerCase();
    if (!q) return base;
    const qPlain = q.replace(/^#/, '');
    return base.filter((g) => {
      const idStr = String(g.conversationId);
      const name = String(g.paciente || '').toLowerCase();
      return idStr.includes(qPlain) || name.includes(q);
    });
  }, [grouped, logSearch, opsFilterConvIds]);

  const logTotal = logFiltered.length;
  const logTotalPages = logPageSize <= 0 ? 1 : Math.max(1, Math.ceil(logTotal / logPageSize));
  const logPageItems = useMemo(() => {
    if (logPageSize <= 0) return logFiltered;
    const start = (logPage - 1) * logPageSize;
    return logFiltered.slice(start, start + logPageSize);
  }, [logFiltered, logPage, logPageSize]);
  const logRangeStart = logTotal === 0 ? 0 : (logPageSize <= 0 ? 1 : (logPage - 1) * logPageSize + 1);
  const logRangeEnd = logPageSize <= 0 ? logTotal : Math.min(logPage * logPageSize, logTotal);

  const convSearchFiltered = useMemo(() => {
    const base = opsFilterConvIds ? grouped.filter((g) => opsFilterConvIds.has(g.conversationId)) : grouped;
    const q = convSearch.trim().toLowerCase();
    if (!q) return base;
    const qPlain = q.replace(/^#/, '');
    return base.filter((g) => {
      const idStr = String(g.conversationId);
      const name = String(g.paciente || '').toLowerCase();
      return idStr.includes(qPlain) || name.includes(q);
    });
  }, [grouped, convSearch, opsFilterConvIds]);

  const convStatusCounts = useMemo(() => {
    const acc = { all: convSearchFiltered.length, abierta: 0, resuelta: 0, reasignada: 0 };
    for (const g of convSearchFiltered) {
      if (g.statusKey in acc) acc[g.statusKey] += 1;
    }
    return acc;
  }, [convSearchFiltered]);

  const convFiltered = useMemo(() => {
    if (convStatus === 'all') return convSearchFiltered;
    return convSearchFiltered.filter((g) => g.statusKey === convStatus);
  }, [convSearchFiltered, convStatus]);

  const convTotal = convFiltered.length;
  const convTotalPages = convPageSize <= 0 ? 1 : Math.max(1, Math.ceil(convTotal / convPageSize));
  const convPageItems = useMemo(() => {
    if (convPageSize <= 0) return convFiltered;
    const start = (convPage - 1) * convPageSize;
    return convFiltered.slice(start, start + convPageSize);
  }, [convFiltered, convPage, convPageSize]);
  const convRangeStart = convTotal === 0 ? 0 : (convPageSize <= 0 ? 1 : (convPage - 1) * convPageSize + 1);
  const convRangeEnd = convPageSize <= 0 ? convTotal : Math.min(convPage * convPageSize, convTotal);

  useEffect(() => { if (logPage > logTotalPages) setLogPage(1); }, [logPage, logTotalPages]);
  useEffect(() => { setLogPage(1); }, [trace, logSearch, logPageSize, opsFilter]);
  useEffect(() => { if (convPage > convTotalPages) setConvPage(1); }, [convPage, convTotalPages]);
  useEffect(() => { setConvPage(1); }, [trace, convSearch, convPageSize, convStatus, opsFilter]);

  useEffect(() => {
    if (sideCollapsed) {
      setLogPageSize(10);
      setLogPage(1);
    }
  }, [sideCollapsed]);

  useEffect(() => {
    if (!opsOpen) return undefined;
    const onDown = (e) => {
      if (e.target.closest('.crm-rtz-ops-inline')) return;
      setOpsOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpsOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [opsOpen]);

  const toggleOps = useCallback(() => {
    setOpsOpen((prev) => {
      const next = !prev;
      if (next && opsBtnRef.current) {
        const r = opsBtnRef.current.getBoundingClientRect();
        setOpsPos({ top: r.bottom + 8, left: r.left });
      }
      return next;
    });
  }, []);

  const toggleLog = useCallback((id) => {
    setOpenLogId((prev) => (prev === id ? null : id));
  }, []);

  const kpis = useMemo(
    () => [
      { label: 'Asignaciones', value: totals.assigned ?? 0, hint: 'En el período', tone: 'create' },
      { label: 'Resueltas', value: totals.resolved ?? 0, hint: 'Cerradas por operador', tone: 'success' },
      { label: 'Abiertas', value: totals.open ?? 0, hint: 'Pendientes de cierre', tone: 'danger' },
      { label: 'Tasa', value: `${totals.resolution_rate_pct ?? 0}%`, hint: 'Resueltas / asignadas', tone: 'assign' },
      { label: 'Prom. cierre', value: formatDurationMinutes(totals.avg_resolution_minutes), hint: 'Asignación → resuelta', tone: 'emit' }
    ],
    [totals]
  );

  const renderStatusPill = (statusKey) => {
    const meta = ASSIGNMENT_STATUS_META[statusKey] || ASSIGNMENT_STATUS_META.abierta;
    return <span className={`crm-trace-status ${meta.cls}`}>{meta.label}</span>;
  };

  return (
    <div className="crm-pl-main crm-ta-rtz-main">
      <div className="crm-gd-toolbar crm-pl-toolbar crm-rtz-toolbar">
        <div className="crm-pl-presets" role="group" aria-label="Período rápido">
          {PERIOD_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`crm-pl-preset${activePreset === preset.id ? ' is-active' : ''}`}
              onClick={() => onPreset(preset.days, preset.id)}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="crm-pl-filters">
          <label className="crm-pl-field crm-pl-field--date">
            <span>Desde</span>
            <input
              type="date"
              value={from}
              onChange={(e) => { onFromChange(e.target.value); onPresetClear(); }}
            />
          </label>
          <label className="crm-pl-field crm-pl-field--date">
            <span>Hasta</span>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => { onToChange(e.target.value); onPresetClear(); }}
            />
          </label>
        </div>

        <div className={`crm-rtz-kpis${loading ? ' is-loading' : ''}`}>
          {kpis.map((kpi) => (
            <div key={kpi.label} className={`crm-rtz-kpi is-${kpi.tone}`} title={kpi.hint}>
              <span className="crm-rtz-kpi__num">{kpi.value}</span>
              <span className="crm-rtz-kpi__label">{kpi.label}</span>
            </div>
          ))}
        </div>
      </div>

      {error ? <div className="crm-pl-alert" role="alert">{error}</div> : null}

      <div className={`crm-pl-split${sideCollapsed ? ' crm-rtz-split--collapsed' : ''}`}>
        <aside className={`crm-pl-side-panel${sideCollapsed ? ' crm-rtz-side--collapsed' : ''}`}>
          {sideCollapsed ? (
            <button
              type="button"
              className="crm-rtz-side-rail"
              onClick={() => setSideCollapsed(false)}
              title="Expandir panel"
              aria-label="Expandir panel de conversaciones"
            >
              <span className="crm-rtz-side-rail__chevron" aria-hidden="true">»</span>
              <span className="crm-rtz-side-rail__icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="4" y="3.5" width="16" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M8 8.5h8M8 12h8M8 15.5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <span className="crm-rtz-side-rail__count">{grouped.length}</span>
            </button>
          ) : (
            <div className="crm-pl-panel-card">
              <div className="crm-pl-panel-head crm-pl-panel-head--compact">
                <h2>Conversaciones del período</h2>
                <div className="crm-rtz-side-head-right">
                  {!loading && operators.length > 0 ? (
                    <div className={`crm-rtz-ops-inline${opsOpen ? ' is-open' : ''}${opsFilter != null ? ' is-filtering' : ''}`}>
                      <button
                        ref={opsBtnRef}
                        type="button"
                        className="crm-rtz-ops-btn"
                        onClick={toggleOps}
                        title={opsFilter != null ? 'Filtrando por operador' : 'Ver operadores'}
                        aria-label="Ver operadores"
                        aria-expanded={opsOpen}
                      >
                        <span className="crm-rtz-ops-btn__ic" aria-hidden="true">👥</span>
                        <span className="crm-rtz-ops-btn__count">{operators.length}</span>
                        {opsFilter != null ? <span className="crm-rtz-ops-btn__dot" aria-hidden="true" /> : null}
                      </button>
                      {opsOpen ? (
                        <div
                          className="crm-rtz-ops-pop"
                          role="dialog"
                          aria-label="Operadores"
                          style={{ top: opsPos.top, left: opsPos.left }}
                        >
                          <div className="crm-rtz-ops-pop__head">
                            <span className="crm-rtz-ops-pop__title">
                              <span aria-hidden="true">👥</span>
                              Operadores
                            </span>
                            <span className="crm-rtz-ops-pop__count">{operators.length}</span>
                            <button
                              type="button"
                              className="crm-rtz-ops-pop__close"
                              onClick={() => setOpsOpen(false)}
                              aria-label="Cerrar"
                            >
                              ✕
                            </button>
                          </div>
                          <p className="crm-rtz-ops-pop__hint">
                            {opsFilter != null ? 'Filtrando conversaciones del operador' : 'Tocá un operador para filtrar'}
                          </p>
                          <div className="crm-rtz-ops-pop__list">
                            <button
                              type="button"
                              className={`crm-rtz-op crm-rtz-op--all${opsFilter == null ? ' is-active' : ''}`}
                              onClick={() => setOpsFilter(null)}
                            >
                              <span className="crm-rtz-op__avatar crm-rtz-op__avatar--all" aria-hidden="true">★</span>
                              <div className="crm-rtz-op__info">
                                <strong className="crm-rtz-op__name">Todos los operadores</strong>
                                <span className="crm-rtz-op__stats">Sin filtro</span>
                              </div>
                              {opsFilter == null ? <span className="crm-rtz-op__check" aria-hidden="true">✓</span> : null}
                            </button>
                            {operators.map((op) => {
                              const label = operatorLabel(op);
                              const avatarName = chatwootNameForMaps(agentLabelMaps, {
                                agentId: op.agent_id,
                                crmLabel: op.operator_name
                              }) || op.operator_name;
                              const active = Number(opsFilter) === Number(op.agent_id);
                              return (
                                <button
                                  key={op.agent_id}
                                  type="button"
                                  className={`crm-rtz-op crm-rtz-op--btn${active ? ' is-active' : ''}`}
                                  title={label}
                                  onClick={() => setOpsFilter(active ? null : op.agent_id)}
                                  aria-pressed={active}
                                >
                                  <span className={`crm-rtz-op__rank${op.rank <= 3 ? ` is-top-${op.rank}` : ''}`}>
                                    {op.rank}
                                  </span>
                                  <span className="crm-rtz-op__avatar" aria-hidden="true">
                                    {getAgentDisplayInitials(avatarName)}
                                  </span>
                                  <div className="crm-rtz-op__info">
                                    <strong className="crm-rtz-op__name">{label}</strong>
                                    <span className="crm-rtz-op__stats">
                                      {op.resolved_count} resueltas · {op.open_count} abiertas · {op.resolution_rate_pct}%
                                    </span>
                                  </div>
                                  <span className="crm-rtz-op__check" aria-hidden="true">{active ? '✓' : ''}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <span className="crm-pl-badge">{grouped.length}</span>
                  <button
                    type="button"
                    className="crm-rtz-side-collapse"
                    onClick={() => setSideCollapsed(true)}
                    title="Colapsar panel"
                    aria-label="Colapsar panel de conversaciones"
                  >
                    «
                  </button>
                </div>
              </div>
              {!loading && grouped.length > 0 ? (
                <div className="crm-rtz-sidetools">
                  <div className="crm-rtz-tablesearch">
                    <span className="crm-rtz-tablesearch__ic" aria-hidden="true">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                        <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="crm-rtz-tablesearch__input"
                      placeholder="Buscar conversación o paciente…"
                      value={convSearch}
                      onChange={(e) => setConvSearch(e.target.value)}
                      aria-label="Buscar conversaciones"
                    />
                    {convSearch ? (
                      <button
                        type="button"
                        className="crm-rtz-tablesearch__clear"
                        onClick={() => setConvSearch('')}
                        aria-label="Limpiar búsqueda"
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                  <label className="crm-rtz-tablesize">
                    <span>Mostrar</span>
                    <div className="crm-rtz-tablesize__wrap">
                      <select
                        className="crm-rtz-tablesize__select"
                        value={convPageSize <= 0 ? 'ALL' : String(convPageSize)}
                        onChange={(e) => {
                          const v = e.target.value;
                          setConvPageSize(v === 'ALL' ? 0 : Number(v));
                        }}
                        aria-label="Conversaciones por página"
                      >
                        <option value="6">6</option>
                        <option value="12">12</option>
                        <option value="24">24</option>
                        <option value="ALL">Todos</option>
                      </select>
                      <span className="crm-rtz-tablesize__caret" aria-hidden="true">▾</span>
                    </div>
                  </label>
                </div>
              ) : null}
              {!loading && grouped.length > 0 ? (
                <div className="crm-rtz-sidechips" role="group" aria-label="Filtrar por estado">
                  {[
                    { id: 'all', label: 'Todas' },
                    { id: 'abierta', label: 'Abiertas' },
                    { id: 'resuelta', label: 'Resueltas' },
                    { id: 'reasignada', label: 'Reasig.' }
                  ].map((chip) => {
                    const cls = chip.id === 'all' ? 'all'
                      : chip.id === 'abierta' ? 'pendiente'
                      : chip.id === 'resuelta' ? 'aprobado'
                      : 'rechazado';
                    return (
                      <button
                        key={chip.id}
                        type="button"
                        className={`crm-rtz-sidechip is-${cls}${convStatus === chip.id ? ' is-active' : ''}`}
                        onClick={() => setConvStatus(chip.id)}
                      >
                        {chip.label}
                        <span className="crm-rtz-sidechip__count">{convStatusCounts[chip.id] ?? 0}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <div className="crm-pl-side-body">
                {loading ? (
                  <TracePanelLoading standalone message="Cargando conversaciones…" />
                ) : grouped.length === 0 ? (
                  <p className="crm-pl-empty">Sin asignaciones para el filtro actual.</p>
                ) : convTotal === 0 ? (
                  <div className="crm-rtz-sidenoresults">
                    <span className="crm-rtz-sidenoresults__ic" aria-hidden="true">🔎</span>
                    <span>
                      {convSearch.trim()
                        ? <>Sin coincidencias para “{convSearch}”.</>
                        : 'Sin conversaciones en este estado.'}
                    </span>
                    <button
                      type="button"
                      className="crm-rtz-sidenoresults__btn"
                      onClick={() => { setConvSearch(''); setConvStatus('all'); }}
                    >
                      Limpiar
                    </button>
                  </div>
                ) : (
                  <div className="crm-pl-pedidos-block">
                    {convPageItems.map((g) => (
                      <div key={g.conversationId} className="crm-pl-pedido-item">
                        <button
                          type="button"
                          className="crm-pl-pedido-item__main"
                          onClick={() => toggleLog(g.conversationId)}
                          title="Ver movimientos"
                        >
                          <div className="crm-pl-pedido-item__top">
                            <span className="crm-pl-pedido-item__id">#{g.conversationId}</span>
                            {renderStatusPill(g.statusKey)}
                          </div>
                          <div className="crm-pl-pedido-item__patient" title={g.paciente || ''}>
                            {g.paciente || 'Sin nombre'}
                          </div>
                          <div className="crm-pl-pedido-item__meta">
                            {g.events.length} mov.
                            {g.latest?.assigned_at ? ` · ${formatDateTimeCompact(g.latest.assigned_at)}` : ''}
                          </div>
                        </button>
                        {onOpenConversation ? (
                          <button
                            type="button"
                            className="crm-pl-icon-btn crm-pl-pedido-item__detail"
                            onClick={() => onOpenConversation(Number(g.conversationId))}
                            title="Abrir conversación"
                            aria-label="Abrir conversación"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="1.8" />
                              <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.8" />
                            </svg>
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!loading && convTotal > 0 ? (
                <div className="crm-rtz-pager crm-rtz-pager--mini">
                  <div className="crm-rtz-pager__meta">
                    {convRangeStart}–{convRangeEnd} de {convTotal}
                  </div>
                  {convTotalPages > 1 ? (
                    <nav className="crm-rtz-pager__nav" aria-label="Paginación de conversaciones">
                      <button
                        type="button"
                        className="crm-rtz-pager__btn"
                        onClick={() => setConvPage((p) => Math.max(1, p - 1))}
                        disabled={convPage <= 1}
                        aria-label="Página anterior"
                      >
                        ‹
                      </button>
                      <span className="crm-rtz-pager__current">{convPage}/{convTotalPages}</span>
                      <button
                        type="button"
                        className="crm-rtz-pager__btn"
                        onClick={() => setConvPage((p) => Math.min(convTotalPages, p + 1))}
                        disabled={convPage >= convTotalPages}
                        aria-label="Página siguiente"
                      >
                        ›
                      </button>
                    </nav>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </aside>

        <section className="crm-pl-table-panel">
          <div className="crm-pl-panel-card">
            <div className="crm-pl-panel-head">
              <div>
                <h2>Registro de asignaciones</h2>
              </div>
              <span className="crm-pl-badge">
                {grouped.length} conversación{grouped.length === 1 ? '' : 'es'}
                {trace.length ? ` · ${trace.length} mov.` : ''}
              </span>
            </div>
            {!loading && trace.length > 0 ? (
              <div className="crm-rtz-tabletools">
                <div className="crm-rtz-tablesearch">
                  <span className="crm-rtz-tablesearch__ic" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="crm-rtz-tablesearch__input"
                    placeholder="Buscar por conversación o paciente…"
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    aria-label="Buscar movimientos"
                  />
                  {logSearch ? (
                    <button
                      type="button"
                      className="crm-rtz-tablesearch__clear"
                      onClick={() => setLogSearch('')}
                      aria-label="Limpiar búsqueda"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
                <label className="crm-rtz-tablesize">
                  <span>Mostrar</span>
                  <div className="crm-rtz-tablesize__wrap">
                    <select
                      className="crm-rtz-tablesize__select"
                      value={logPageSize <= 0 ? 'ALL' : String(logPageSize)}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLogPageSize(v === 'ALL' ? 0 : Number(v));
                      }}
                      aria-label="Conversaciones por página"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="ALL">Todos</option>
                    </select>
                    <span className="crm-rtz-tablesize__caret" aria-hidden="true">▾</span>
                  </div>
                </label>
              </div>
            ) : null}
            <div className="crm-pl-table-scroll">
              {loading ? (
                <TracePanelLoading standalone message="Cargando asignaciones…" />
              ) : trace.length === 0 ? (
                <p className="crm-pl-empty">No hay registros para el filtro actual.</p>
              ) : logTotal === 0 ? (
                <div className="crm-rtz-noresults">
                  <span className="crm-rtz-noresults__ic" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <div className="crm-rtz-noresults__title">Sin coincidencias</div>
                  <div className="crm-rtz-noresults__text">
                    No encontramos conversaciones para “{logSearch}”.
                  </div>
                  <button
                    type="button"
                    className="crm-rtz-noresults__btn"
                    onClick={() => setLogSearch('')}
                  >
                    Limpiar búsqueda
                  </button>
                </div>
              ) : (
                <div className={`crm-rtz-log${sideCollapsed ? ' crm-rtz-log--grid' : ''}`}>
                  {logPageItems.map((group) => {
                    const isOpen = openLogId === group.conversationId;
                    return (
                      <article
                        className={`crm-rtz-log-card${isOpen ? ' is-open' : ''}`}
                        key={`ta-group-${group.conversationId}`}
                      >
                        <div
                          className="crm-rtz-log-card__head"
                          role="button"
                          tabIndex={0}
                          aria-expanded={isOpen}
                          onClick={() => toggleLog(group.conversationId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleLog(group.conversationId);
                            }
                          }}
                        >
                          <span className="crm-rtz-log-card__chevron" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                          <div className="crm-rtz-log-card__idline">
                            <span className="crm-rtz-log-card__id">#{group.conversationId}</span>
                            {renderStatusPill(group.statusKey)}
                            <span className="crm-rtz-log-card__count">{group.events.length} mov.</span>
                          </div>
                          <div className="crm-rtz-log-card__patient">
                            <span className="crm-rtz-log-card__name" title={group.paciente || ''}>
                              {group.paciente || 'Sin nombre'}
                            </span>
                          </div>
                          {onOpenConversation ? (
                            <button
                              type="button"
                              className="crm-pl-icon-btn crm-rtz-log-card__eye"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenConversation(Number(group.conversationId));
                              }}
                              title="Abrir conversación"
                              aria-label="Abrir conversación"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="1.8" />
                                <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.8" />
                              </svg>
                            </button>
                          ) : null}
                        </div>
                        <div className="crm-rtz-log-card__body">
                          <div className="crm-rtz-log-card__body-inner">
                            <ol className="crm-rtz-log-steps">
                              {group.events.map((row) => {
                                const sk = assignmentStatusKey(row);
                                const meta = ASSIGNMENT_STATUS_META[sk];
                                const assigneeLabel = formatUserWithChatwootName(
                                  row.assignee_display_name,
                                  agentLabelMaps,
                                  { agentId: row.assignee_agent_id }
                                );
                                const assignerLabel = formatUserWithChatwootName(
                                  row.assigned_by_username,
                                  agentLabelMaps
                                );
                                const actor = assignerLabel && assignerLabel !== '—' ? assignerLabel : assigneeLabel;
                                return (
                                  <li key={row.id} className={`crm-rtz-log-step is-${meta.tone}`}>
                                    <span className="crm-rtz-log-step__marker" aria-hidden="true">
                                      {meta.icon}
                                    </span>
                                    <div className="crm-rtz-log-step__content">
                                      <div className="crm-rtz-log-step__row">
                                        <span className="crm-rtz-log-step__title">{meta.label}</span>
                                        <span className="crm-rtz-log-step__time">
                                          {formatDateTimeCompact(row.assigned_at)}
                                        </span>
                                      </div>
                                      <div className="crm-rtz-log-step__targets">
                                        {assigneeLabel && assigneeLabel !== '—' ? (
                                          <span className="crm-rtz-log-step__target">→ {assigneeLabel}</span>
                                        ) : null}
                                        {sk === 'resuelta' && row.resolution_minutes != null ? (
                                          <span className="crm-rtz-log-step__target">
                                            ⏱ {formatDurationMinutes(row.resolution_minutes)}
                                          </span>
                                        ) : null}
                                      </div>
                                      <div className="crm-rtz-log-step__actor">
                                        <span className="crm-rtz-log-step__avatar" aria-hidden="true">
                                          {getAgentDisplayInitials(actor)}
                                        </span>
                                        <span className="crm-rtz-log-step__actor-name">{actor}</span>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ol>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
            {!loading && logTotal > 0 ? (
              <div className="crm-rtz-pager">
                <div className="crm-rtz-pager__meta">
                  Mostrando <strong>{logRangeStart}</strong>–<strong>{logRangeEnd}</strong> de <strong>{logTotal}</strong> conversación{logTotal === 1 ? '' : 'es'}
                </div>
                {logTotalPages > 1 ? (
                  <nav className="crm-rtz-pager__nav" aria-label="Paginación de asignaciones">
                    <button
                      type="button"
                      className="crm-rtz-pager__btn"
                      onClick={() => setLogPage(1)}
                      disabled={logPage <= 1}
                      aria-label="Primera página"
                    >
                      «
                    </button>
                    <button
                      type="button"
                      className="crm-rtz-pager__btn"
                      onClick={() => setLogPage((p) => Math.max(1, p - 1))}
                      disabled={logPage <= 1}
                      aria-label="Página anterior"
                    >
                      ‹
                    </button>
                    <span className="crm-rtz-pager__current">
                      Página <strong>{logPage}</strong> de {logTotalPages}
                    </span>
                    <button
                      type="button"
                      className="crm-rtz-pager__btn"
                      onClick={() => setLogPage((p) => Math.min(logTotalPages, p + 1))}
                      disabled={logPage >= logTotalPages}
                      aria-label="Página siguiente"
                    >
                      ›
                    </button>
                    <button
                      type="button"
                      className="crm-rtz-pager__btn"
                      onClick={() => setLogPage(logTotalPages)}
                      disabled={logPage >= logTotalPages}
                      aria-label="Última página"
                    >
                      »
                    </button>
                  </nav>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

function TracePanelBody({
  standalone = false,
  from,
  to,
  assigneeId,
  activePreset,
  loading,
  loadingAgents,
  error,
  agentOptions,
  agentLabelMaps,
  kpiCards,
  operators,
  trace,
  onFromChange,
  onToChange,
  onAssigneeChange,
  onPreset,
  onRefresh,
  onOpenConversation
}) {
  const presetsBlock = (
    <div
      className={standalone ? 'crm-ta-presets' : 'crm-trace-presets'}
      role="group"
      aria-label="Período rápido"
    >
      {PERIOD_PRESETS.map((preset) => (
        <button
          key={preset.id}
          type="button"
          className={
            standalone
              ? `crm-ta-preset${activePreset === preset.id ? ' is-active' : ''}`
              : `crm-trace-preset${activePreset === preset.id ? ' is-active' : ''}`
          }
          onClick={() => onPreset(preset.days, preset.id)}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );

  const filtersBlock = (
    <div className={standalone ? 'crm-ta-filters' : 'crm-trace-filters'}>
      <label className={standalone ? 'crm-ta-field' : 'crm-trace-field'}>
        <span>Desde</span>
        <input
          type="date"
          value={from}
          onChange={(e) => {
            onFromChange(e.target.value);
            onPreset(null, '');
          }}
        />
      </label>
      <label className={standalone ? 'crm-ta-field' : 'crm-trace-field'}>
        <span>Hasta</span>
        <input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(e) => {
            onToChange(e.target.value);
            onPreset(null, '');
          }}
        />
      </label>
      <label
        className={
          standalone
            ? 'crm-ta-field crm-ta-field--operator'
            : 'crm-trace-field crm-trace-field--operator'
        }
      >
        <span>Operador</span>
        <select
          value={assigneeId}
          onChange={(e) => onAssigneeChange(e.target.value)}
          disabled={loadingAgents && !agentOptions.length}
        >
          <option value="">Todos los operadores</option>
          {agentOptions.map((agent) => (
            <option key={agent.id} value={String(agent.id)}>
              {formatUserWithChatwootName(agent.crm_username || agent.name, agentLabelMaps, {
                agentId: agent.id
              })}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  const panelContent = (
    <>
      <div
        className={
          standalone
            ? 'crm-gd-toolbar crm-ta-toolbar'
            : 'crm-toolbar crm-trace-toolbar'
        }
      >
        {presetsBlock}
        {filtersBlock}
        <button
          type="button"
          className={standalone ? 'crm-gd-btn crm-gd-btn--primary' : 'crm-btn crm-btn-primary crm-trace-refresh'}
          onClick={() => void onRefresh()}
          disabled={loading}
        >
          {loading ? 'Actualizando…' : 'Actualizar'}
        </button>
      </div>

      {error ? (
        <div className={standalone ? 'crm-ta-alert' : 'crm-trace-alert'} role="alert">
          {error}
        </div>
      ) : null}

      {standalone ? (
        <div className={`crm-gd-kpi-grid crm-ta-kpis${loading ? ' is-loading' : ''}`}>
          {kpiCards.map((kpi) => (
            <div key={kpi.label} className="crm-gd-kpi-card">
              <div className="crm-gd-kpi-card__num">{kpi.value}</div>
              <div className="crm-gd-kpi-card__label">{kpi.label}</div>
              {kpi.hint ? (
                <div style={{ marginTop: 6, fontSize: 11, color: '#64748b', lineHeight: 1.35 }}>
                  {kpi.hint}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className={`crm-kpi-row crm-trace-kpis${loading ? ' is-loading' : ''}`}>
          {kpiCards.map((kpi) => (
            <div key={kpi.label} className="crm-kpi crm-trace-kpi">
              <div className="crm-kpi-lbl">{kpi.label}</div>
              <div className="crm-kpi-val">{kpi.value}</div>
              {kpi.hint ? (
                <div className={`crm-kpi-ch${kpi.tone ? ` ${kpi.tone}` : ''}`}>{kpi.hint}</div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <div className={standalone ? 'crm-ta-split' : 'crm-trace-split'}>
        <aside className={standalone ? 'crm-ta-rank-panel' : 'crm-trace-rank-panel'}>
          <div className={standalone ? 'crm-ta-panel-card' : 'crm-trace-rank-card'}>
            <div className={standalone ? 'crm-ta-panel-head' : 'crm-trace-panel-head'}>
              <div>
                <h2>Ranking de operadores</h2>
                <p>Resueltas, abiertas y tiempo promedio de cierre.</p>
              </div>
              <span className={standalone ? 'crm-ta-badge' : 'crm-trace-table-card__badge'}>
                {operators.length}
              </span>
            </div>
            <div className={standalone ? 'crm-ta-rank-body' : 'crm-trace-rank-card__body'}>
              {loading ? (
                <TracePanelLoading standalone={standalone} message="Cargando ranking…" />
              ) : operators.length === 0 ? (
                <p className={standalone ? 'crm-ta-empty' : 'crm-trace-empty'}>
                  Sin asignaciones en este período.
                </p>
              ) : (
                <ul className="crm-trace-rank-list">
                  {operators.map((op) => {
                    const operatorLabel = formatUserWithChatwootName(
                      op.operator_name,
                      agentLabelMaps,
                      { agentId: op.agent_id }
                    );
                    const avatarName =
                      chatwootNameForMaps(agentLabelMaps, {
                        agentId: op.agent_id,
                        crmLabel: op.operator_name
                      }) || op.operator_name;

                    return (
                    <li key={op.agent_id} className="crm-trace-rank-item">
                      <span
                        className={`crm-trace-rank-pos${op.rank <= 3 ? ` is-top-${op.rank}` : ''}`}
                        aria-label={`Puesto ${op.rank}`}
                      >
                        {op.rank}
                      </span>
                      <span className="crm-trace-rank-avatar" aria-hidden="true">
                        {getAgentDisplayInitials(avatarName)}
                      </span>
                      <div className="crm-trace-rank-info">
                        <strong title={operatorLabel}>{operatorLabel}</strong>
                        <span>
                          {op.resolved_count} resueltas · {op.open_count} abiertas
                        </span>
                        <div className="crm-trace-rank-bar" aria-hidden="true">
                          <span
                            className="crm-trace-rank-bar__fill"
                            style={{
                              width: `${Math.min(100, Math.max(0, Number(op.resolution_rate_pct) || 0))}%`
                            }}
                          />
                        </div>
                      </div>
                      <div className="crm-trace-rank-stats">
                        <span className="crm-trace-rank-stats__rate">{op.resolution_rate_pct}%</span>
                        <span className="crm-trace-rank-stats__avg">
                          {formatDurationMinutes(op.avg_resolution_minutes)}
                        </span>
                      </div>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </aside>

        <section className={standalone ? 'crm-ta-table-panel' : 'crm-trace-table-panel'}>
          <div className={standalone ? 'crm-ta-panel-card' : 'crm-trace-table-card'}>
            <div className={standalone ? 'crm-ta-panel-head' : 'crm-trace-panel-head'}>
              <div>
                <h2>Detalle de asignaciones</h2>
                <p>Registro cronológico con paciente, operador y estado en Chatwoot.</p>
              </div>
              <span className={standalone ? 'crm-ta-badge' : 'crm-trace-table-card__badge'}>
                {trace.length} registro{trace.length === 1 ? '' : 's'}
              </span>
            </div>
            <div
              className={
                standalone
                  ? 'crm-ta-table-scroll'
                  : 'crm-tscroll crm-trace-tscroll'
              }
            >
              {loading ? (
                <TracePanelLoading
                  standalone={standalone}
                  message="Cargando asignaciones…"
                />
              ) : trace.length === 0 ? (
                <p className={standalone ? 'crm-ta-empty' : 'crm-trace-empty crm-trace-empty--table'}>
                  No hay registros para el filtro actual.
                </p>
              ) : (
                <div className="datatable-table">
                  <table className="hs-table crm-trace-table">
                    <thead>
                      <tr>
                        <th>Paciente</th>
                        <th>Operador</th>
                        <th>Fecha y hora</th>
                        <th>Estado</th>
                        <th>Tiempo</th>
                        <th aria-label="Acciones" />
                      </tr>
                    </thead>
                    <tbody>
                      {trace.map((row) => {
                        const assigneeLabel = formatUserWithChatwootName(
                          row.assignee_display_name,
                          agentLabelMaps,
                          { agentId: row.assignee_agent_id }
                        );
                        const assignerLabel = formatUserWithChatwootName(
                          row.assigned_by_username,
                          agentLabelMaps
                        );

                        return (
                        <tr key={row.id}>
                          <td className="crm-trace-table__patient" title={row.patient_display_name || ''}>
                            {row.patient_display_name || 'Sin nombre'}
                          </td>
                          <td title={assigneeLabel}>{assigneeLabel}</td>
                          <td className="crm-trace-table__muted">{formatDateTime(row.assigned_at)}</td>
                          <td>
                            <span className={`crm-trace-status ${statusClass(row)}`}>
                              {statusLabel(row)}
                            </span>
                          </td>
                          <td className="crm-trace-table__muted">
                            {formatDurationMinutes(row.resolution_minutes)}
                          </td>
                          <td className="crm-trace-table__actions">
                            {onOpenConversation ? (
                              <button
                                type="button"
                                className={
                                  standalone
                                    ? 'crm-gd-btn crm-gd-btn--ghost'
                                    : 'crm-btn crm-btn-outline crm-trace-open-btn'
                                }
                                onClick={() => onOpenConversation(Number(row.conversation_id))}
                              >
                                Ver chat
                              </button>
                            ) : null}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );

  return standalone ? <div className="crm-ta-body">{panelContent}</div> : panelContent;
}
